import React, { useState, useRef } from 'react';
import { stateConfig, funnelGroups, isMetaSource } from './types';
import type { MarketingLead, Message } from './types';
import LeadEmailProposalModal from './LeadEmailProposalModal';
import { getCountryInfo } from '../Outreach';

interface Props {
    lead: MarketingLead;
    messages: Message[];
    loadingMessages: boolean;
    updatingState: boolean;
    convertingPlan: 'professional' | 'basic' | null;
    onClose: () => void;
    onStateChange: (phone: string, newState: string) => void;
    onConvert: (phone: string, plan: 'professional' | 'basic') => void;
}

const LeadDetailModal: React.FC<Props> = ({
    lead, messages, loadingMessages, updatingState, convertingPlan,
    onClose, onStateChange, onConvert,
}) => {
    const [activeTab, setActiveTab] = useState<'info' | 'chat'>('info');
    const [showEmailProposal, setShowEmailProposal] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const cfg = stateConfig[lead.state] || { label: lead.state, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', icon: 'person' };
    const isPlanActive = lead.state === 'PLAN_ACTIVE';
    const canConvert = !isPlanActive;

    const scoreFields = [
        { key: 'decisionRole', label: 'Autoridad', values: { owner: 'Dueño', decision_maker: 'Decisor', no_decision: 'Sin decisión' } },
        { key: 'dailyVolume', label: 'Volumen/día', values: { low: '<10 msgs', medium: '10–30 msgs', high: '>30 msgs' } },
        { key: 'followUpBehavior', label: 'Seguimiento', values: { lost: 'Se pierde', manual: 'Manual', unsure: 'No sabe' } },
        { key: 'intentLevel', label: 'Intención', values: { ready: 'Implementar pronto', exploring: 'Solo explorando' } },
    ] as const;
    const hasScoreData = scoreFields.some(f => !!(lead as any)[f.key]);

    return (
        <>
            <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4">
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => onClose()} />
                <div className="relative w-full sm:max-w-lg bg-white dark:bg-slate-card rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col">

                            {/* Handle */}
                            <div className="flex justify-center pt-3 sm:hidden">
                                <div className="w-10 h-1 bg-slate-300 rounded-full" />
                            </div>

                            {/* Modal Header */}
                            <div className="flex-none flex items-center justify-between px-5 pt-4 pb-4 border-b border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <div className={`w-11 h-11 rounded-full ${cfg.bg} flex items-center justify-center`}>
                                        <span className={`material-symbols-outlined text-[22px] ${cfg.color}`}>{cfg.icon}</span>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                            {lead.name || lead.phone}
                                        </h3>
                                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                            {(() => {
                                                const countryInfo = getCountryInfo(lead);
                                                return (
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${countryInfo.badgeClass}`}>
                                                        <span>{countryInfo.flag}</span>
                                                        {countryInfo.name}
                                                    </span>
                                                );
                                            })()}
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                                                {cfg.label}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => onClose()}
                                    className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-600"
                                >
                                    <span className="material-symbols-outlined text-[18px]">close</span>
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="flex-none flex p-2 gap-1 bg-slate-100 dark:bg-slate-800">
                                <button
                                    onClick={() => setActiveTab('info')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                        activeTab === 'info'
                                            ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">info</span>
                                    Info
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('chat');
                                        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }), 80);
                                    }}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
                                        activeTab === 'chat'
                                            ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">forum</span>
                                    Conversacion
                                    {messages.length > 0 && (
                                        <span className="text-[10px] font-bold bg-primary text-white px-1.5 py-0.5 rounded-full ml-1">
                                            {messages.length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Tab: Conversacion */}
                            {activeTab === 'chat' && (
                                <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-900/40 px-4 py-4 flex flex-col gap-2">
                                    {loadingMessages ? (
                                        <div className="flex items-center justify-center py-12 text-slate-400">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                <span className="text-xs">Cargando conversacion...</span>
                                            </div>
                                        </div>
                                    ) : messages.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                                            <span className="material-symbols-outlined text-[40px] mb-2 text-slate-300">chat_bubble</span>
                                            <p className="text-sm font-medium">Sin mensajes registrados</p>
                                            <p className="text-xs text-slate-400 mt-1">La conversacion aparecera aqui cuando el lead escriba</p>
                                        </div>
                                    ) : (
                                        <>
                                            {messages.map((msg, i) => {
                                                const isUser = msg.role === 'user';
                                                const showDate = i === 0 || new Date(msg.createdAt).toDateString() !== new Date(messages[i - 1].createdAt).toDateString();
                                                return (
                                                    <React.Fragment key={msg.id}>
                                                        {showDate && (
                                                            <div className="flex items-center gap-2 my-2">
                                                                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                                                                <span className="text-[10px] font-semibold text-slate-400 px-2">
                                                                    {new Date(msg.createdAt).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                                </span>
                                                                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                                                            </div>
                                                        )}
                                                        <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                                                            <div className={`max-w-[82%] ${isUser ? 'order-2' : 'order-1'}`}>
                                                                <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                                                                    isUser
                                                                        ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-tl-sm shadow-sm'
                                                                        : 'bg-primary text-white rounded-tr-sm shadow-sm'
                                                                }`}>
                                                                    {msg.content}
                                                                </div>
                                                                <p className={`text-[10px] text-slate-400 mt-0.5 ${isUser ? 'text-left pl-1' : 'text-right pr-1'}`}>
                                                                    {new Date(msg.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                                                    {!isUser && <span className="ml-1 text-primary/60">Diana</span>}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Tab: Info */}
                            {activeTab === 'info' && (
                            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                                {/* Quick Actions */}
                                <div className="flex gap-2">
                                    <a
                                        href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-brand-accent/90 text-white rounded-xl text-sm font-semibold hover:bg-brand-accent transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">chat</span>
                                        WhatsApp
                                    </a>
                                    <button
                                        onClick={() => navigator.clipboard.writeText(lead.phone)}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                        {lead.phone}
                                    </button>
                                </div>

                                    {/* Botón y Aviso de Plantilla de Correo Personalizado (Resend) */}
                                    {(() => {
                                        const text = `${lead.followupNotes || ''} ${lead.leadSummary || ''}`;
                                        const isEmailSent = /propuesta\s+(?:enviada|por\s+correo|remitida)|correo\s+enviado|email\s+enviado|✉️\s*propuesta/i.test(text);
                                        const matchWithTo = text.match(/✉️\s*Propuesta enviada por correo a\s+([^\s(]+)(?:\s*\(([^)]+)\))?/i)
                                                         || text.match(/Propuesta enviada\s+(?:por correo\s+)?a\s+([^\s(]+)(?:\s*\(([^)]+)\))?/i);
                                        const sentDetails = matchWithTo ? `Enviado a ${matchWithTo[1]}${matchWithTo[2] ? ` (${matchWithTo[2]})` : ''}` : 'Propuesta enviada previamente';

                                        return (
                                            <div className="space-y-2">
                                                {isEmailSent && (
                                                    <div className="rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-3 flex items-start gap-2.5 text-xs text-emerald-900 dark:text-emerald-200">
                                                        <span className="material-symbols-outlined text-[20px] text-emerald-600 dark:text-emerald-400 shrink-0">mark_email_read</span>
                                                        <div className="flex-1">
                                                            <p className="font-bold text-emerald-950 dark:text-emerald-100 flex items-center gap-1.5">
                                                                ✓ Plantilla de Propuesta ya enviada
                                                            </p>
                                                            <p className="text-[11px] text-emerald-800/90 dark:text-emerald-300/90 mt-0.5 leading-relaxed">
                                                                {sentDetails}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                <button
                                                    onClick={() => setShowEmailProposal(true)}
                                                    className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-[0.99] ${
                                                        isEmailSent
                                                            ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'
                                                            : 'bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:opacity-95 shadow-amber-500/20'
                                                    }`}
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">
                                                        {isEmailSent ? 'task_alt' : 'mark_email_read'}
                                                    </span>
                                                    {isEmailSent ? '✓ Propuesta Enviada — Ver / Reenviar' : 'Plantilla de Propuesta y Correo (Resend)'}
                                                </button>
                                            </div>
                                        );
                                    })()}

                                {/* Resumen Ejecutivo de la Conversación (IA) */}
                                {(lead.leadSummary || lead.followupNotes) && (
                                    <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800/60 bg-gradient-to-br from-indigo-50/90 via-purple-50/40 to-white dark:from-indigo-950/30 dark:via-purple-950/20 dark:to-slate-card p-4 space-y-2 shadow-sm">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[18px] text-indigo-600 dark:text-indigo-400">auto_awesome</span>
                                                Resumen Ejecutivo de Diana
                                            </p>
                                            {lead.outreachStatus && (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    lead.outreachReplied 
                                                        ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300/60' 
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                                }`}>
                                                    {lead.outreachReplied ? '🔥 Respondido a Prospección' : '✉️ Prospección Enviada'}
                                                </span>
                                            )}
                                        </div>
                                        {lead.leadSummary && (
                                            <p className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed font-medium whitespace-pre-wrap">
                                                {lead.leadSummary}
                                            </p>
                                        )}
                                        {lead.followupNotes && (
                                            <div className="pt-2 mt-2 border-t border-indigo-100 dark:border-indigo-900/40 flex items-start gap-1.5 text-[11px] text-indigo-950 dark:text-indigo-200">
                                                <span className="material-symbols-outlined text-[14px] text-indigo-500 shrink-0 mt-0.5">contact_phone</span>
                                                <span><strong>Contactos / Notas:</strong> {lead.followupNotes}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Info Grid */}
                                <div className="grid grid-cols-2 gap-2">
                                    {lead.clinicName && (
                                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                                            <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Clínica</p>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">{lead.clinicName}</p>
                                        </div>
                                    )}
                                    {lead.clinicCity && (
                                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                                            <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Ciudad</p>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">{lead.clinicCity}{lead.clinicCountry ? `, ${lead.clinicCountry}` : ''}</p>
                                        </div>
                                    )}
                                    {lead.email && (
                                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 col-span-2">
                                            <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Email</p>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">{lead.email}</p>
                                        </div>
                                    )}
                                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                                        <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Fuente</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{lead.source}</p>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3">
                                        <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Registrado</p>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{new Date(lead.createdAt).toLocaleDateString('es-ES')}</p>
                                    </div>
                                </div>

                                {/* D — Meta Ads Attribution Card */}
                                {isMetaSource(lead) && (
                                    <div className="rounded-xl border border-blue-200 dark:border-blue-700/40 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-2">
                                        <p className="text-xs font-bold text-blue-700 dark:text-blue-400 flex items-center gap-1.5">
                                            <span className="w-5 h-5 rounded bg-blue-600 text-white flex items-center justify-center font-black text-[11px]">f</span>
                                            Captado vía Meta Ads
                                        </p>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <p className="text-blue-400 uppercase font-semibold text-[10px]">Canal</p>
                                                <p className="font-semibold text-blue-800 dark:text-blue-200 capitalize mt-0.5">
                                                    {lead.source?.includes('instagram') ? 'Instagram' : lead.source?.includes('facebook') || lead.source?.includes('meta') ? 'Facebook' : lead.source}
                                                </p>
                                            </div>
                                            {lead.adCampaignId && (
                                                <div>
                                                    <p className="text-blue-400 uppercase font-semibold text-[10px]">Campaign ID</p>
                                                    <p className="font-semibold text-blue-800 dark:text-blue-200 mt-0.5 font-mono text-[11px] break-all">{lead.adCampaignId}</p>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-blue-500 dark:text-blue-400">
                                            Ver CPL y métricas de campaña en <span className="font-semibold">Meta Ads → Funnel</span>
                                        </p>
                                    </div>
                                )}

                                {/* Scoring Variables */}
                                {hasScoreData && (
                                    <div className="bg-primary/8 dark:bg-primary/20 rounded-xl p-4 border border-primary/12 dark:border-primary">
                                        <p className="text-xs font-bold text-primary dark:text-primary mb-3 flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[16px]">analytics</span>
                                            Variables de calificación
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {scoreFields.map(f => {
                                                const raw = (lead as any)[f.key] as string | undefined;
                                                if (!raw) return null;
                                                return (
                                                    <div key={f.key} className="bg-white dark:bg-slate-800 rounded-lg p-2.5">
                                                        <p className="text-[10px] text-slate-400 uppercase font-semibold">{f.label}</p>
                                                        <p className="text-sm font-semibold text-slate-900 dark:text-white mt-0.5">
                                                            {(f.values as Record<string, string>)[raw] || raw}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Plan Active Info */}
                                {isPlanActive && lead.convertedAt && (
                                    <div className={`rounded-xl p-4 border ${lead.conversionPlan === 'professional' ? 'bg-primary/8 border-primary/20 dark:bg-primary/20 dark:border-primary' : 'bg-slate-50 border-slate-200 dark:bg-slate-700/50 dark:border-slate-600'}`}>
                                        <p className="text-sm font-bold mb-1 flex items-center gap-2 text-slate-800 dark:text-white">
                                            <span className="material-symbols-outlined text-[16px]">workspace_premium</span>
                                            Cliente desde {new Date(lead.convertedAt).toLocaleDateString('es-ES')}
                                        </p>
                                        <p className="text-xs text-slate-600 dark:text-slate-400">
                                            Plan: <strong>{lead.conversionPlan === 'professional' ? 'Pro — $179/mes' : 'Básico — $99/mes'}</strong>
                                        </p>
                                        {lead.conversionPlan === 'basic' && (
                                            <p className="text-xs text-slate-500 mt-1">Incluye: respuestas automáticas y agenda. Sin seguimiento automático.</p>
                                        )}
                                    </div>
                                )}

                                {/* Trial Info */}
                                {lead.trialStartedAt && (
                                    <div className="bg-brand-accent/8 dark:bg-brand-accent/20 rounded-xl p-4 border border-brand-accent/20 dark:border-brand-accent">
                                        <p className="text-sm font-bold text-brand-accent dark:text-brand-accent mb-3 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[16px]">science</span>
                                            Trial Plan Pro
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                            <div>
                                                <p className="text-xs text-brand-accent dark:text-brand-accent">Inicio</p>
                                                <p className="text-sm font-semibold text-brand-accent dark:text-brand-accent">{new Date(lead.trialStartedAt).toLocaleDateString('es-ES')}</p>
                                            </div>
                                            {lead.trialEndsAt && (
                                                <div>
                                                    <p className="text-xs text-brand-accent dark:text-brand-accent">Vence</p>
                                                    <p className="text-sm font-semibold text-brand-accent dark:text-brand-accent">{new Date(lead.trialEndsAt).toLocaleDateString('es-ES')}</p>
                                                </div>
                                            )}
                                        </div>
                                        {lead.trialConversationsLimit > 0 && (
                                            <div>
                                                <div className="flex justify-between text-xs text-brand-accent dark:text-brand-accent mb-1">
                                                    <span>Conversaciones usadas</span>
                                                    <span>{lead.trialConversationsCount}/{lead.trialConversationsLimit}</span>
                                                </div>
                                                <div className="h-2 bg-brand-accent/20 dark:bg-brand-accent rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${(lead.trialConversationsCount / lead.trialConversationsLimit) >= 0.8 ? 'bg-amber-500' : 'bg-brand-accent/90'}`}
                                                        style={{ width: `${Math.min(100, (lead.trialConversationsCount / lead.trialConversationsLimit) * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Auto-reminders status */}
                                {(() => {
                                    const allMsgs = [
                                        { key: 'silenceFollowupSent',  dateKey: 'silenceFollowupSentAt',  label: 'Silencio 24h (sin respuesta)' },
                                        { key: 'silenceFollowup2Sent', dateKey: 'silenceFollowup2SentAt', label: 'Silencio 48h (último intento)' },
                                        { key: 'reminderDay12Sent',    dateKey: 'reminderDay12SentAt',    label: 'Recordatorio día 12' },
                                        { key: 'usageWarning80Sent',   dateKey: 'usageWarning80SentAt',   label: 'Aviso 80% contactos únicos' },
                                        { key: 'usageWarning95Sent',   dateKey: 'usageWarning95SentAt',   label: 'Aviso 95% contactos únicos' },
                                        { key: 'usageLimitSent',       dateKey: 'usageLimitSentAt',       label: 'Límite de 100 contactos' },
                                        { key: 'followup24hSent',      dateKey: 'followup24hSentAt',      label: 'Seguimiento 24h post-trial' },
                                        { key: 'followup48hSent',      dateKey: 'followup48hSentAt',      label: 'Seguimiento 48h post-trial' },
                                    ];
                                    const relevant = allMsgs.filter(m => {
                                        // Silencio solo si no tiene trial; el resto solo si tiene trial
                                        if (m.key === 'silenceFollowupSent' || m.key === 'silenceFollowup2Sent') return !lead.trialStartedAt;
                                        return !!lead.trialStartedAt;
                                    });
                                    const hasSent = relevant.some(m => (lead as any)[m.key]);
                                    if (!hasSent && relevant.every(m => !(lead as any)[m.key])) {
                                        // Mostrar igualmente para que el usuario vea el estado
                                    }
                                    return (
                                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                                            <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[16px] text-amber-500">notifications</span>
                                                Mensajes automáticos
                                            </p>
                                            <div className="space-y-2">
                                                {relevant.map(({ key, dateKey, label }) => {
                                                    const sent = !!(lead as any)[key];
                                                    const date = (lead as any)[dateKey] as string | undefined;
                                                    return (
                                                        <div key={key} className="flex items-center gap-3">
                                                            <span className={`material-symbols-outlined text-[18px] ${sent ? 'text-brand-accent' : 'text-slate-300 dark:text-slate-600'}`}>
                                                                {sent ? 'check_circle' : 'radio_button_unchecked'}
                                                            </span>
                                                            <span className={`text-sm flex-1 ${sent ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>{label}</span>
                                                            {sent && date && <span className="text-xs text-slate-400">{new Date(date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Manual State Change */}
                                <div>
                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[14px] text-primary">swap_horiz</span>
                                        Cambiar estado manualmente
                                    </p>
                                    <div className="space-y-3">
                                        {funnelGroups.map(group => (
                                            <div key={group.label}>
                                                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${group.color}`}>{group.label}</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {group.states.map(st => {
                                                        const c = stateConfig[st];
                                                        const isActive = lead.state === st;
                                                        return (
                                                            <button
                                                                key={st}
                                                                disabled={isActive || updatingState}
                                                                onClick={() => onStateChange(lead.phone, st)}
                                                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${
                                                                    isActive
                                                                        ? `${c.bg} ${c.color} ${c.border} ring-2 ring-offset-1 ring-current`
                                                                        : `${c.bg} ${c.color} ${c.border} opacity-50 hover:opacity-100`
                                                                } disabled:cursor-not-allowed`}
                                                            >
                                                                {isActive && 'âœ“ '}{c.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                            )}

                            {/* Footer fijo — Convertir a cliente (solo tab Info) */}
                            {activeTab === 'info' && canConvert && (
                                <div className="flex-none px-5 pb-4 pt-3 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-card">
                                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Convertir a cliente</p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => onConvert(lead.phone, 'professional')}
                                            disabled={convertingPlan !== null}
                                            className="flex-1 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-primary transition-colors disabled:opacity-60 shadow-lg shadow-primary/20"
                                        >
                                            {convertingPlan === 'professional' ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined text-[16px]">workspace_premium</span>
                                            )}
                                            Pro $179
                                        </button>
                                        <button
                                            onClick={() => onConvert(lead.phone, 'basic')}
                                            disabled={convertingPlan !== null}
                                            className="flex-1 py-2.5 bg-slate-600 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-slate-700 transition-colors disabled:opacity-60"
                                        >
                                            {convertingPlan === 'basic' ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined text-[16px]">star</span>
                                            )}
                                            Básico $99
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {showEmailProposal && (
                        <LeadEmailProposalModal
                            lead={lead}
                            onClose={() => setShowEmailProposal(false)}
                        />
                    )}
        </>
    );
};

export default LeadDetailModal;

